import { gql } from '@apollo/client';
import {
  AnalysisContainerFieldsFragment,
  CreatorFields,
  DatePickerFieldsFragment,
  UserAliasQueryMeasureFragment,
  UserAliasQueryStepFieldsFragment,
  UserAliasReplacementFieldsFragment,
} from '../fragments';
import {
  DataTableQueryFieldsFragment,
  ExpressionQueryFieldsFragment,
  FunnelQueryFieldsFragment,
  PathQueryFieldsFragment,
  PredictionQueryFieldsFragment,
  RetentionQueryFieldsFragment,
} from '../builderFragments';
import {
  CohortDataFieldsFragment,
  CohortSampleQueryFragment,
  UserAliasQueryCohortFragment,
} from '../cohorts';
import { SignificanceQueryFieldsFragment } from '../significance/fragments';
import {
  LlmChatMessageFieldsFragment,
  LlmSubjectSummaryFieldsFragment,
} from '../llm/fragments';
import { RichTextFieldsFragment } from '../richText/fragments';
import { IncidentFieldsFragment } from '../../../pages/Incidents/apollo/fragments';
import { SignificanceDataFieldsFragment } from '../significance/significanceAnalysis';

/**
 * Common Fragments
 */

export const SliceNameHintFieldsFragment = gql`
  fragment SliceNameHintFields on SliceNameHint {
    name
    value
  }
`;

export const SliceUserAliasFieldsFragment = gql`
  fragment SliceUserAliasFields on UserAliasSlice {
    ... on UserAliasSliceBase {
      aliasId
    }
    ... on UserAliasSliceBreakdown {
      aliasId
      groupBys
    }
  }
`;

export const HighlightFieldsFragment = gql`
  fragment HighlightFields on Highlight {
    date
    text
    value
  }
`;

export const MovingAverageFieldsFragment = gql`
  fragment MovingAverageFields on MovingAverage {
    values
    display
  }
`;

export const MovingSumFieldsFragment = gql`
  fragment MovingSumFields on MovingSum {
    values
    display
  }
`;

export const ReadingFieldsFragment = gql`
  fragment ReadingFields on Reading {
    ma {
      ...MovingAverageFields
    }
    ma30 {
      ...MovingAverageFields
    }
    ms7 {
      ...MovingSumFields
    }
    ms30 {
      ...MovingSumFields
    }
    highlights {
      ...HighlightFields
    }
  }
  ${MovingAverageFieldsFragment}
  ${MovingSumFieldsFragment}
  ${HighlightFieldsFragment}
`;

export const CumulativeSumFieldsFragment = gql`
  fragment CumulativeSumFields on ExpressionCumulativeSum {
    display
    values
  }
`;

export const ChartControlFieldsFragment = gql`
  fragment ChartControlFields on ChartControl {
    default
    measureControls {
      name
      key
      charts
      isResultKey
      isCountFunction
      userAlias {
        ... on UserAliasQueryMeasure {
          ...UserAliasQueryMeasureFields
        }
        ... on UserAliasQueryStep {
          ...UserAliasQueryStepFields
        }
        ... on UserAliasQueryCohort {
          ...UserAliasQueryCohortFields
        }
      }
    }
  }
  ${UserAliasQueryMeasureFragment}
  ${UserAliasQueryStepFieldsFragment}
  ${UserAliasQueryCohortFragment}
`;

export const DateIntervalFieldsFragment = gql`
  fragment DateIntervalFields on DateInterval {
    start
    end
  }
`;

export const CohortDateFieldsFragment = gql`
  fragment CohortDateFields on CohortDate {
    date
    interval {
      ...DateIntervalFields
    }
  }
  ${DateIntervalFieldsFragment}
`;

export const AnalysisPrefFieldsFragment = gql`
  fragment AnalysisPrefFields on AnalysisPref {
    chartPref
    aliasReplacements {
      ...UserAliasReplacementFields
    }
  }
  ${UserAliasReplacementFieldsFragment}
`;

export const AnalysisRichTextSummaryFieldsFragment = gql`
  fragment AnalysisRichTextSummaryFields on AnalysisRichTextSummary {
    richText {
      ...RichTextFields
    }
    showInDashboard
  }
  ${RichTextFieldsFragment}
`;

export const AnalysisMetaFieldsFragment = gql`
  fragment AnalysisMetaFields on Analysis {
    id
    display
    description
    pref {
      ...AnalysisPrefFields
    }
    kind
    start
    end
    timeUnit
    queryType
    groupbys
    live
    status
    errorStatus
    failureMsg
    exportUrl
    executedAt
    estimatedDuration
    referenceSql
    createdAt
    isFavorite
    creator {
      ...CreatorFields
    }
    datePicker {
      ...DatePickerFields
    }
    llmSummary {
      ...LlmSubjectSummaryFields
    }
    richTextSummary {
      ...AnalysisRichTextSummaryFields
    }
    container {
      ...AnalysisContainerFields
    }
  }
  ${AnalysisPrefFieldsFragment}
  ${CreatorFields}
  ${DatePickerFieldsFragment}
  ${LlmSubjectSummaryFieldsFragment}
  ${AnalysisRichTextSummaryFieldsFragment}
  ${AnalysisContainerFieldsFragment}
`;

export const FlowFieldsFragment = gql`
  fragment FlowFields on Flow {
    name
    key
    rank
    steps {
      currentEvent
      nextEvent
      seq
      count
    }
    userAlias {
      ...SliceUserAliasFields
    }
    hint {
      ...SliceNameHintFields
    }
  }

  ${SliceUserAliasFieldsFragment}
  ${SliceNameHintFieldsFragment}
`;

export const TopPathsSliceFieldsFragment = gql`
  fragment TopPathsSliceFields on TopPathsSlice {
    name
    key
    rank
    userAlias {
      ...SliceUserAliasFields
    }
    hint {
      ...SliceNameHintFields
    }
    topPaths {
      rank
      funnel {
        ...AnalysisMetaFields
        data {
          ...FunnelDataFields
        }
        chartControl {
          ...ChartControlFields
        }
        query {
          ...FunnelQueryFields
        }
      }
    }
  }
`;

export const PathDataFieldsFragment = gql`
  fragment PathDataFields on PathData {
    modeValue {
      ... on SankeyPathModeValue {
        base {
          ...FlowFields
        }
        flows {
          ...FlowFields
        }
      }
      ... on TopPathsPathModeValue {
        base {
          ...TopPathsSliceFields
        }
        slices {
          ...TopPathsSliceFields
        }
      }
    }
    isEmpty
  }
  ${FlowFieldsFragment}
  ${TopPathsSliceFieldsFragment}
`;
// =========================== End of Path ===========================

export const RetentionSliceFieldsFragment = gql`
  fragment RetentionSliceFields on RetentionSlice {
    name
    key
    rowIndices
    rank
    hint {
      ...SliceNameHintFields
    }
    userAlias {
      ...SliceUserAliasFields
    }
  }
  ${SliceUserAliasFieldsFragment}
  ${SliceNameHintFieldsFragment}
`;

export const RetentionLiveFieldsFragment = gql`
  fragment RetentionLiveFields on RetentionLive {
    cohortLive
    retentionLive
  }
`;

export const RetentionRowFieldsFragment = gql`
  fragment RetentionRowFields on RetentionRow {
    name
    key
    startIdentities
    returnIdentities
    churnIdentities
    retentions
    churns
  }
`;

const RetentionIntervalFieldsFragment = gql`
  fragment RetentionIntervalFields on RetentionInterval {
    start
    end
  }
`;

const RetentionCohortStartFieldsFragment = gql`
  fragment RetentionCohortStartFields on RetentionCohortStart {
    key
    name
    startInterval {
      ...RetentionIntervalFields
    }
    actualInterval {
      ...RetentionIntervalFields
    }
  }
  ${RetentionIntervalFieldsFragment}
`;

const RetentionStandardAverageFieldsFragment = gql`
  fragment RetentionStandardAverageFields on RetentionStandardAverage {
    key
    name
    rowIndices
  }
`;

const ReturnPeriodFieldsFragment = gql`
  fragment ReturnPeriodFields on ReturnPeriod {
    key
    name
  }
`;

const CohortStartRowFieldsFragment = gql`
  fragment CohortStartRowFields on CohortStartRow {
    rowIndices
    cohortStart {
      ...RetentionCohortStartFields
    }
    returnIntervals {
      ...RetentionIntervalFields
    }
  }
  ${RetentionCohortStartFieldsFragment}
  ${RetentionIntervalFieldsFragment}
`;

const ReturnPeriodRowFieldsFragment = gql`
  fragment ReturnPeriodRowFields on ReturnPeriodRow {
    rowIndices
    returnIntervals {
      ...RetentionIntervalFields
    }
    returnPeriod {
      ...ReturnPeriodFields
    }
  }
  ${RetentionIntervalFieldsFragment}
  ${ReturnPeriodFieldsFragment}
`;

const RetentionStandardTableRowSelectorFieldsFragment = gql`
  fragment RetentionStandardTableRowSelectorFields on RetentionStandardTableRowSelector {
    standardColumns: columns {
      ...ReturnPeriodFields
    }
    standardRows: rows {
      ...CohortStartRowFields
    }
    avgRetention {
      ...RetentionStandardAverageFields
    }
  }
  ${ReturnPeriodFieldsFragment}
  ${CohortStartRowFieldsFragment}
  ${RetentionStandardAverageFieldsFragment}
`;

const RetentionOverTimeTableRowSelectorFieldsFragment = gql`
  fragment RetentionOverTimeTableRowSelectorFields on RetentionOverTimeTableRowSelector {
    overTimeColumns: columns {
      ...RetentionCohortStartFields
    }
    overTimeRows: rows {
      ...ReturnPeriodRowFields
    }
  }
  ${RetentionCohortStartFieldsFragment}
  ${ReturnPeriodRowFieldsFragment}
`;

export const RetentionDataFieldsFragment = gql`
  fragment RetentionDataFields on RetentionData {
    retentionLive: live {
      ...RetentionLiveFields
    }
    rows {
      ...RetentionRowFields
    }
    base {
      ...RetentionSliceFields
    }
    slices {
      ...RetentionSliceFields
    }
    tableRowSelector {
      __typename
      ... on RetentionStandardTableRowSelector {
        ...RetentionStandardTableRowSelectorFields
      }
      ... on RetentionOverTimeTableRowSelector {
        ...RetentionOverTimeTableRowSelectorFields
      }
    }
    isEmpty
  }
  ${RetentionLiveFieldsFragment}
  ${RetentionRowFieldsFragment}
  ${RetentionSliceFieldsFragment}
  ${RetentionStandardTableRowSelectorFieldsFragment}
  ${RetentionOverTimeTableRowSelectorFieldsFragment}
`;
// =========================== End of Retention ===========================

/**
 * Prediction Implements Analysis
 * Separate Meta and Data fields if we only need meta fields
 */
export const CorrelationKindFieldsFragment = gql`
  fragment CorrelationKindFields on CorrelationKind {
    key
    display
  }
`;

export const CorrelationFieldsFragment = gql`
  fragment CorrelationFields on Correlation {
    correlation
    marginOfError
    predictionLevel
    ppv
    npv
    specificity
    sensitivity
    pat
  }
`;
export const UserStatusFieldsFragment = gql`
  fragment UserStatusFields on UserStatus {
    totalUsers
    usersPerformedEvent
    percentage
  }
`;
export const ContingencyFieldsFragment = gql`
  fragment ContingencyFields on Contingency {
    truePositive
    falsePositive
    trueNegative
    falseNegative
  }
`;
export const EventFrequencyCorrelationFieldsFragment = gql`
  fragment EventFrequencyCorrelationFields on EventFrequencyCorrelation {
    correlation {
      ...CorrelationFields
    }
    userStatus {
      ...UserStatusFields
    }
    contingency {
      ...ContingencyFields
    }
  }
  ${CorrelationFieldsFragment}
  ${UserStatusFieldsFragment}
  ${ContingencyFieldsFragment}
`;
export const EventDayCorrelationFieldsFragment = gql`
  fragment EventDayCorrelationFields on EventDayCorrelation {
    day
    strongestEventFrequencyCorrelation
    frequencyCorrelations {
      ...EventFrequencyCorrelationFields
    }
  }
  ${EventFrequencyCorrelationFieldsFragment}
`;

export const PredictionDataFieldsFragment = gql`
  fragment PredictionDataFields on PredictionData {
    correlationKinds {
      ...CorrelationKindFields
    }
    events
    eventCorrelations {
      event
      eventDayCorrelations {
        ...EventDayCorrelationFields
      }
    }
    isEmpty
  }
  ${CorrelationKindFieldsFragment}
  ${EventDayCorrelationFieldsFragment}
`;
// =========================== End of Prediction ===========================

export const ExpressionColumnFieldsFragment = gql`
  fragment ExpressionColumnFields on ExpressionColumn {
    name
    measureKey
    key
    values
    basePercents
    percentsPreviousValue
    cumulativeSum {
      ...CumulativeSumFields
    }
    reading {
      ...ReadingFields
    }
  }
  ${ReadingFieldsFragment}
  ${CumulativeSumFieldsFragment}
`;

export const ExpressionSliceFieldsFragment = gql`
  fragment ExpressionSliceFields on ExpressionSlice {
    name
    key
    variables
    rank
    hint {
      ...SliceNameHintFields
    }
    userAlias {
      ...SliceUserAliasFields
    }
    resultMeasureColumnIndex
  }
  ${SliceNameHintFieldsFragment}
  ${SliceUserAliasFieldsFragment}
`;

export const ExpressionStatisticsFieldsFragment = gql`
  fragment ExpressionStatisticsFields on ExpressionStatistics {
    min
    max
    avg
    stddev
    median
    percentile
  }
`;

const QueryModeXAxisFieldsFragment = gql`
  fragment QueryModeXAxisFields on QueryModeXAxis {
    cohortDates {
      ...CohortDateFields
    }
  }
  ${CohortDateFieldsFragment}
`;

const HistogramModeXAxisFieldsFragment = gql`
  fragment HistogramModeXAxisFields on HistogramModeXAxis {
    buckets
  }
`;

const ImpactAnalysisModeXAxisFieldsFragment = gql`
  fragment ImpactAnalysisModeXAxisFields on ImpactAnalysisModeXAxis {
    periods
  }
`;

const ExpressionModeXAxisFieldsFragment = gql`
  fragment ExpressionModeXAxisFields on ExpressionModeXAxis {
    ... on QueryModeXAxis {
      ...QueryModeXAxisFields
    }
    ... on HistogramModeXAxis {
      ...HistogramModeXAxisFields
    }
    ... on ImpactAnalysisModeXAxis {
      ...ImpactAnalysisModeXAxisFields
    }
  }
  ${QueryModeXAxisFieldsFragment}
  ${HistogramModeXAxisFieldsFragment}
  ${ImpactAnalysisModeXAxisFieldsFragment}
`;

export const ExpressionDataFieldsFragment = gql`
  fragment ExpressionDataFields on ExpressionData {
    dates
    expressionAxis: xAxis {
      ...ExpressionModeXAxisFields
    }
    live
    columns {
      ...ExpressionColumnFields
    }
    base {
      ...ExpressionSliceFields
    }
    slices {
      ...ExpressionSliceFields
    }
    statistics {
      ...ExpressionStatisticsFields
    }
    isEmpty
  }
  ${ExpressionStatisticsFieldsFragment}
  ${ExpressionColumnFieldsFragment}
  ${ExpressionSliceFieldsFragment}
  ${ExpressionModeXAxisFieldsFragment}
`;
// ========================= End of Expression =========================

export const DataTableColumnFieldsFragment = gql`
  fragment DataTableColumnFields on DataTableColumn {
    basePercents
    key
    measureKey
    name
    values
  }
`;

export const DataTableSliceFieldsFragment = gql`
  fragment DataTableSliceFields on DataTableSlice {
    key
    measures
    name
    rank
    userAlias {
      ...SliceUserAliasFields
    }
    hint {
      ...SliceNameHintFields
    }
  }
  ${SliceUserAliasFieldsFragment}
  ${SliceNameHintFieldsFragment}
`;

export const DataTableDataFieldsFragment = gql`
  fragment DataTableDataFields on DataTableData {
    dates
    live
    columns {
      ...DataTableColumnFields
    }
    base {
      ...DataTableSliceFields
    }
    slices {
      ...DataTableSliceFields
    }
    isEmpty
  }
  ${DataTableColumnFieldsFragment}
  ${DataTableSliceFieldsFragment}
`;

export const FunnelPercentFieldsFragment = gql`
  fragment FunnelPercentFields on FunnelPercent {
    key
    values
    bounceCounts
    bouncePercents
    reading {
      ...ReadingFields
    }
  }
  ${ReadingFieldsFragment}
`;
export const FunnelColumnFieldsFragment = gql`
  fragment FunnelColumnFields on FunnelColumn {
    name
    key
    measureKey
    sliceKey
    funnelModeSliceKey
    values
    percents {
      ...FunnelPercentFields
    }
    statistics {
      avg
      median
    }
  }
  ${FunnelPercentFieldsFragment}
`;
export const FunnelSliceFieldsFragment = gql`
  fragment FunnelSliceFields on FunnelSlice {
    name
    key
    measure
    variables
    rank
    hint {
      ...SliceNameHintFields
    }
    userAlias {
      ...SliceUserAliasFields
    }
  }
  ${SliceNameHintFieldsFragment}
  ${SliceUserAliasFieldsFragment}
`;

export const FunnelDistributionBucketFieldsFragment = gql`
  fragment FunnelDistributionBucketFields on FunnelDistributionBucket {
    startInclusive
    endExclusive
  }
`;

export const FunnelFrequencyXAxisFieldsFragment = gql`
  fragment FunnelFrequencyXAxisFields on FunnelFrequencyXAxis {
    buckets {
      ...FunnelDistributionBucketFields
    }
  }
  ${FunnelDistributionBucketFieldsFragment}
`;

export const FunnelTimeToConvertXAxisFieldsFragment = gql`
  fragment FunnelTimeToConvertXAxisFields on FunnelTimeToConvertXAxis {
    buckets {
      ...FunnelDistributionBucketFields
    }
    timeUnit
  }
  ${FunnelDistributionBucketFieldsFragment}
`;

export const FunnelConversionXAxisFieldsFragment = gql`
  fragment FunnelConversionXAxisFields on FunnelConversionXAxis {
    cohortDates {
      ...CohortDateFields
    }
  }
  ${CohortDateFieldsFragment}
`;

export const FunnelXAxisFieldsFragment = gql`
  fragment FunnelXAxisFields on FunnelXAxis {
    ... on FunnelFrequencyXAxis {
      ...FunnelFrequencyXAxisFields
    }
    ... on FunnelTimeToConvertXAxis {
      ...FunnelTimeToConvertXAxisFields
    }
    ... on FunnelConversionXAxis {
      ...FunnelConversionXAxisFields
    }
  }
  ${FunnelFrequencyXAxisFieldsFragment}
  ${FunnelTimeToConvertXAxisFieldsFragment}
  ${FunnelConversionXAxisFieldsFragment}
`;

export const FunnelDataFieldsFragment = gql`
  fragment FunnelDataFields on FunnelData {
    dates
    live
    columns {
      ...FunnelColumnFields
    }
    funnelXAxis: xAxis {
      ...FunnelXAxisFields
    }
    base {
      ...FunnelSliceFields
    }
    slices {
      ...FunnelSliceFields
    }
    isEmpty
  }
  ${FunnelSliceFieldsFragment}
  ${FunnelColumnFieldsFragment}
  ${FunnelXAxisFieldsFragment}
`;

// ========================= End of Funnel =========================

export const ComparisonConfigFieldsFragment = gql`
  fragment ComparisonConfigFields on ComparisonConfig {
    offset
    timeUnit
  }
`;

/**
 * DO NOT SELECT `data` field in this fragment for performance reasons.
 * See the usage in GET_ANALYSIS_INFO for more details
 */
export const AnalysisInfoFieldsFragment = gql`
  fragment AnalysisInfoFields on Analysis {
    id
    display
    description
    executedAt
    queryType
    start
    end
    timeUnit
    creator {
      ...CreatorFields
    }
    datePicker {
      ...DatePickerFields
    }
    ... on Expression {
      query {
        ...ExpressionQueryFields
      }
      comparisonConfig {
        ...ComparisonConfigFields
      }
    }
    ... on Funnel {
      query {
        ...FunnelQueryFields
      }
    }
    ... on Path {
      query {
        ...PathQueryFields
      }
    }
    ... on Retention {
      query {
        ...RetentionQueryFields
      }
    }
    ... on Prediction {
      query {
        ...PredictionQueryFields
      }
    }
    ... on DataTable {
      query {
        ...DataTableQueryFields
      }
      comparisonConfig {
        ...ComparisonConfigFields
      }
    }
    ... on Significance {
      query {
        ...SignificanceQueryFields
      }
    }
    ... on CohortSample {
      query {
        ...CohortSampleQueryFields
      }
    }
  }
  ${CreatorFields}
  ${ExpressionQueryFieldsFragment}
  ${FunnelQueryFieldsFragment}
  ${PathQueryFieldsFragment}
  ${RetentionQueryFieldsFragment}
  ${PredictionQueryFieldsFragment}
  ${DataTableQueryFieldsFragment}
  ${SignificanceQueryFieldsFragment}
  ${CohortSampleQueryFragment}
  ${ComparisonConfigFieldsFragment}
  ${DatePickerFieldsFragment}
`;

export const AnalysisSummaryFieldsFragment = gql`
  fragment AnalysisSummaryInfoFields on Analysis {
    id
    schema {
      id
    }
    queryType
    display
    createdAt
    description
    pref {
      chartPref
    }
  }
`;

export const PopulateBuilderAnalysisFieldsFragment = gql`
  fragment PopulateBuilderAnalysisFields on Analysis {
    ...AnalysisMetaFields
    ... on Expression {
      geo
      comparisonConfig {
        ...ComparisonConfigFields
      }
      query {
        ...ExpressionQueryFields
      }
    }

    ... on DataTable {
      comparisonConfig {
        ...ComparisonConfigFields
      }
      query {
        ...DataTableQueryFields
      }
    }

    ... on Funnel {
      query {
        ...FunnelQueryFields
      }
    }

    ... on Path {
      query {
        ...PathQueryFields
      }
    }

    ... on Retention {
      query {
        ...RetentionQueryFields
      }
    }

    ... on Prediction {
      query {
        ...PredictionQueryFields
      }
    }

    ... on CohortSample {
      query {
        ...CohortSampleQueryFields
      }
    }

    ... on Significance {
      query {
        ...SignificanceQueryFields
      }
    }
  }
  ${AnalysisMetaFieldsFragment}
  ${ExpressionQueryFieldsFragment}
  ${DataTableQueryFieldsFragment}
  ${FunnelQueryFieldsFragment}
  ${PathQueryFieldsFragment}
  ${RetentionQueryFieldsFragment}
  ${PredictionQueryFieldsFragment}
  ${CohortSampleQueryFragment}
  ${SignificanceQueryFieldsFragment}
  ${ComparisonConfigFieldsFragment}
`;

export const CohortSampleTemplateFieldsFragment = gql`
  fragment CohortSampleTemplateFields on CohortSampleTemplate {
    datePicker {
      ...DatePickerFields
    }
    start
    end
    query {
      ...CohortSampleQueryFields
    }
  }
  ${DatePickerFieldsFragment}
  ${CohortSampleQueryFragment}
`;

export const ExpressionTemplateFieldsFragment = gql`
  fragment ExpressionTemplateFields on ExpressionTemplate {
    datePicker {
      ...DatePickerFields
    }
    start
    end
    query {
      ...ExpressionQueryFields
    }
    timeUnit
    comparisonConfig {
      ...ComparisonConfigFields
    }
  }
  ${DatePickerFieldsFragment}
  ${ExpressionQueryFieldsFragment}
  ${ComparisonConfigFieldsFragment}
`;

export const FunnelTemplateFieldsFragment = gql`
  fragment FunnelTemplateFields on FunnelTemplate {
    datePicker {
      ...DatePickerFields
    }
    start
    end
    query {
      ...FunnelQueryFields
    }
    timeUnit
  }
  ${DatePickerFieldsFragment}
  ${FunnelQueryFieldsFragment}
`;

export const PathTemplateFieldsFragment = gql`
  fragment PathTemplateFields on PathTemplate {
    datePicker {
      ...DatePickerFields
    }
    start
    end
    query {
      ...PathQueryFields
    }
  }
  ${DatePickerFieldsFragment}
  ${PathQueryFieldsFragment}
`;

export const RetentionTemplateFieldsFragment = gql`
  fragment RetentionTemplateFields on RetentionTemplate {
    datePicker {
      ...DatePickerFields
    }
    start
    end
    query {
      ...RetentionQueryFields
    }
  }
  ${DatePickerFieldsFragment}
  ${RetentionQueryFieldsFragment}
`;

export const DataTableTemplateFieldsFragment = gql`
  fragment DataTableTemplateFields on DataTableTemplate {
    datePicker {
      ...DatePickerFields
    }
    start
    end
    query {
      ...DataTableQueryFields
    }
    comparisonConfig {
      ...ComparisonConfigFields
    }
  }
  ${DatePickerFieldsFragment}
  ${DataTableQueryFieldsFragment}
  ${ComparisonConfigFieldsFragment}
`;

export const SignificanceTemplateFieldsFragment = gql`
  fragment SignificanceTemplateFields on SignificanceTemplate {
    datePicker {
      ...DatePickerFields
    }
    start
    end
    query {
      ...SignificanceQueryFields
    }
  }
  ${DatePickerFieldsFragment}
  ${SignificanceQueryFieldsFragment}
`;

const FormulaTemplateFieldsFragment = gql`
  fragment FormulaTemplateFields on FormulaTemplate {
    ... on CohortSampleTemplate {
      ...CohortSampleTemplateFields
    }
    ... on ExpressionTemplate {
      ...ExpressionTemplateFields
    }
    ... on FunnelTemplate {
      ...FunnelTemplateFields
    }
    ... on PathTemplate {
      ...PathTemplateFields
    }
    ... on RetentionTemplate {
      ...RetentionTemplateFields
    }
    ... on DataTableTemplate {
      ...DataTableTemplateFields
    }
    ... on SignificanceTemplate {
      ...SignificanceTemplateFields
    }
  }
  ${CohortSampleTemplateFieldsFragment}
  ${ExpressionTemplateFieldsFragment}
  ${FunnelTemplateFieldsFragment}
  ${PathTemplateFieldsFragment}
  ${RetentionTemplateFieldsFragment}
  ${DataTableTemplateFieldsFragment}
  ${SignificanceTemplateFieldsFragment}
`;

export const LlmCreateFormulaTemplateFieldsFragment = gql`
  fragment LlmCreateFormulaTemplateFields on LlmCreateFormulaTemplate {
    template {
      ...FormulaTemplateFields
    }
    promptMessage {
      ...LlmChatMessageFields
    }
    responseMessage {
      ...LlmChatMessageFields
    }
  }
  ${LlmChatMessageFieldsFragment}
  ${FormulaTemplateFieldsFragment}
`;

export const AnalysisFieldsFragment = gql`
  fragment AnalysisFields on Analysis {
    ... on Expression {
      geo
      data @skip(if: $populateBuilder) {
        ...ExpressionDataFields
      }
      comparisonData @skip(if: $populateBuilder) {
        ...ExpressionDataFields
      }
      comparisonConfig {
        ...ComparisonConfigFields
      }
      chartControl {
        ...ChartControlFields
      }
      incidents {
        ...IncidentFields
      }
      query {
        ...ExpressionQueryFields
      }
    }

    ... on DataTable {
      data @skip(if: $populateBuilder) {
        ...DataTableDataFields
      }
      comparisonData @skip(if: $populateBuilder) {
        ...DataTableDataFields
      }
      comparisonConfig {
        ...ComparisonConfigFields
      }
      chartControl {
        ...ChartControlFields
      }
      query {
        ...DataTableQueryFields
      }
    }

    ... on Funnel {
      data @skip(if: $populateBuilder) {
        ...FunnelDataFields
      }
      chartControl {
        ...ChartControlFields
      }
      incidents {
        ...IncidentFields
      }
      query {
        ...FunnelQueryFields
      }
    }

    ... on Path {
      data @skip(if: $populateBuilder) {
        ...PathDataFields
      }
      query {
        ...PathQueryFields
      }
    }

    ... on Retention {
      data @skip(if: $populateBuilder) {
        ...RetentionDataFields
      }
      query {
        ...RetentionQueryFields
      }
    }

    ... on Prediction {
      data @skip(if: $populateBuilder) {
        ...PredictionDataFields
      }
      query {
        ...PredictionQueryFields
      }
    }

    ... on CohortSample {
      data @skip(if: $populateBuilder) {
        ...CohortDataFields
      }
      query {
        ...CohortSampleQueryFields
      }
    }

    ... on Significance {
      data @skip(if: $populateBuilder) {
        ...SignificanceDataFields
      }
      query {
        ...SignificanceQueryFields
      }
    }
  }

  ${ChartControlFieldsFragment}
  ${ExpressionDataFieldsFragment}
  ${IncidentFieldsFragment}
  ${ExpressionQueryFieldsFragment}
  ${DataTableDataFieldsFragment}
  ${DataTableQueryFieldsFragment}
  ${FunnelDataFieldsFragment}
  ${FunnelQueryFieldsFragment}
  ${PathQueryFieldsFragment}
  ${PathDataFieldsFragment}
  ${RetentionQueryFieldsFragment}
  ${RetentionDataFieldsFragment}
  ${PredictionDataFieldsFragment}
  ${PredictionQueryFieldsFragment}
  ${CohortSampleQueryFragment}
  ${CohortDataFieldsFragment}
  ${SignificanceDataFieldsFragment}
  ${SignificanceQueryFieldsFragment}
  ${ComparisonConfigFieldsFragment}
`;
